import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEvents, getEventById } from "../../services/eventService";
import { assignDevice } from "../../services/linkedDeviceService";
import Button from "./Button";
import Modal from "./Modal";
import { color } from "../../styles/theme";
import { toast } from "react-toastify";

// Assigns (or reassigns) a linked device to show a specific event's QR.
// QR type options are gated by that event's own QR configuration:
// eQR needs the event to have a QR image, lQR needs a location QR set on the event.
const AssignDeviceModal = ({ device, onClose, onAssigned }) => {
  const { t } = useTranslation("common");
  const QR_TYPE_LABELS = {
    eQR: t("assignDeviceModal.qrType.eQR"),
    pQR: t("assignDeviceModal.qrType.pQR"),
    lQR: t("assignDeviceModal.qrType.lQR"),
  };
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(device?.CurrentAssignment?.EventId?.toString() || "");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [qrType, setQrType] = useState(device?.CurrentAssignment?.QRType || "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getEvents()
      .then((res) => setEvents(res.data?.Result || []))
      .catch(() => toast.error(t("assignDeviceModal.loadEventsError")))
      .finally(() => setEventsLoading(false));
  }, [t]);

  useEffect(() => {
    if (!selectedEventId) {
      setSelectedEvent(null);
      return;
    }

    setEventLoading(true);
    getEventById(selectedEventId)
      .then((res) => setSelectedEvent(res.data || null))
      .catch(() => toast.error(t("assignDeviceModal.loadEventDetailsError")))
      .finally(() => setEventLoading(false));
  }, [selectedEventId, t]);

  const availableQrTypes = ["pQR"]
    .concat(selectedEvent?.QRCodeImageLink ? ["eQR"] : [])
    .concat(selectedEvent?.LQRId ? ["lQR"] : []);

  useEffect(() => {
    if (qrType && !availableQrTypes.includes(qrType)) {
      setQrType("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEventId) { toast.warning(t("assignDeviceModal.selectEventWarning")); return; }
    if (!qrType) { toast.warning(t("assignDeviceModal.selectQrTypeWarning")); return; }

    setSubmitting(true);
    try {
      const res = await assignDevice({
        linkedDeviceId: device.Id,
        eventId: Number(selectedEventId),
        qrType,
        locationQRCodeId: qrType === "lQR" ? selectedEvent?.LQRId : null,
      });
      if (res.data?.Success) {
        toast.success(res.data?.Message || t("assignDeviceModal.assignSuccess"));
        onAssigned();
        onClose();
      } else {
        toast.error(res.data?.Message || t("assignDeviceModal.assignError"));
      }
    } catch (err) {
      toast.error(err.response?.data?.Message || t("assignDeviceModal.assignError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t("assignDeviceModal.title")}
      subtitle={device?.DeviceName || device?.DeviceIdentifier}
      onClose={onClose}
      busy={submitting}
      maxWidth={460}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting} type="button">{t("actions.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={submitting}>{submitting ? t("assignDeviceModal.assigning") : t("assignDeviceModal.assign")}</Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div style={s.field}>
          <label style={s.label}>{t("assignDeviceModal.eventLabel")}</label>
          <select value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)} className="input" disabled={eventsLoading} required>
            <option value="">{eventsLoading ? t("assignDeviceModal.loadingEvents") : t("assignDeviceModal.selectEvent")}</option>
            {events.map((ev) => (
              <option key={ev.Id} value={ev.Id}>{ev.Name}</option>
            ))}
          </select>
        </div>

        <div style={s.field}>
          <label style={s.label}>{t("assignDeviceModal.qrTypeLabel")}</label>
          <select value={qrType} onChange={(e) => setQrType(e.target.value)} className="input" disabled={!selectedEventId || eventLoading} required>
            <option value="">{eventLoading ? t("status.loading") : t("assignDeviceModal.selectQrType")}</option>
            {availableQrTypes.map((type) => (
              <option key={type} value={type}>{QR_TYPE_LABELS[type]}</option>
            ))}
          </select>
          {selectedEventId && !eventLoading && availableQrTypes.length === 1 && (
            <div style={s.hintSmall}>
              {t("assignDeviceModal.onlyPersonalQrHint")}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

const s = {
  hintSmall: { marginTop: 6, fontSize: 12, color: color.textMuted },
  field: { marginBottom: 16 },
  label: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
};

export default AssignDeviceModal;
